import { NetworkStatus, useQuery } from "@apollo/client";
import { useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";

import NotFoundPage from "components/error/NotFoundPage";
import PageLoading from "components/loading/PageLoading";
import Button from "ds/components/Button";
import useErrorHandle from "hooks/useErrorHandle";
import useCanManageExternalIntegration from "shared/ExternalIntegration/access/useCanManageExternalIntegration";

import IntegrationActionDropdown from "./ActionDropdown";
import { showIntegrationDetailsDrawer } from "./Drawer";
import { GET_EXTERNAL_INTEGRATION } from "./gql";
import PageLayout from "./PageLayout";
import { ExternalIntegrationQueryResponse } from "./types";
import IntegrationUsedBy from "./UsedBy";

const ServiceNowIntegrationDetailsPage = () => {
  const params = useParams<{ integrationId?: string }>();
  const canManageExternalIntegration = useCanManageExternalIntegration();
  const navigate = useNavigate();

  const { integrationId } = params;

  const { loading, data, networkStatus, error } = useQuery<ExternalIntegrationQueryResponse>(
    GET_EXTERNAL_INTEGRATION,
    {
      variables: {
        integrationId,
      },
    }
  );

  const openDetailsDrawer = useCallback(() => {
    if (data?.externalIntegration) {
      showIntegrationDetailsDrawer({
        integration: data.externalIntegration,
        id: data.externalIntegration.id,
      });
    }
  }, [data?.externalIntegration]);

  const onDelete = useCallback(() => {
    navigate(`/integrations/servicenow`);
  }, [navigate]);

  const ErrorContent = useErrorHandle(error);

  const canManageIntegration = useMemo(() => {
    if (!data?.externalIntegration) {
      return false;
    }
    return canManageExternalIntegration(data.externalIntegration);
  }, [data?.externalIntegration, canManageExternalIntegration]);

  const actions = useMemo(() => {
    return data?.externalIntegration ? (
      <>
        <Button variant="secondary" onPress={openDetailsDrawer}>
          Details
        </Button>
        <IntegrationActionDropdown
          size="medium"
          integration={data.externalIntegration}
          refetchQueriesOnDelete={["GetExternalIntegrations"]}
          canManageIntegration={canManageIntegration}
          showDetails={false}
          onDelete={onDelete}
        />
      </>
    ) : null;
  }, [data?.externalIntegration, canManageIntegration, openDetailsDrawer, onDelete]);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && networkStatus !== NetworkStatus.refetch && !data?.externalIntegration) {
    return <PageLoading />;
  }

  if (!integrationId || !data?.externalIntegration) {
    return <NotFoundPage />;
  }

  return (
    <PageLayout actions={actions} data={data}>
      <IntegrationUsedBy
        name={data.externalIntegration.name}
        attachedBluePrints={data.externalIntegration.attachedBlueprints}
        loading={loading}
      />
    </PageLayout>
  );
};

export default ServiceNowIntegrationDetailsPage;
